/* LocaleContext — single source of truth for persona/locale/currency on the
   UniBuy™ storefront. Exposes:
     - persona           current persona object (NL / BR / CN)
     - personaKey        "nl" | "br" | "cn"
     - locale            BCP-47 tag for Intl APIs ("en" | "pt-BR" | "zh-CN")
     - currency          ISO 4217 ("EUR" | "BRL" | "CNY")
     - rate              EUR -> display currency multiplier (hardcoded demo FX)
     - setPersonaKey()   switches persona (also writes localStorage)
     - t(key)            translates against /i18n/translations.js
     - fmt(eurAmount)    converts from EUR and formats with Intl

   Backend prices and totals are stored in EUR.  We convert at render only —
   never on the backend, never mutating the payload.
*/

import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { PERSONAS, DEFAULT_PERSONA_KEY } from "./personas";
import { tr } from "./translations";

const STORAGE_KEY = "infoshop:persona";

const LocaleContext = createContext(null);

function readInitialPersonaKey() {
  if (typeof window === "undefined") return DEFAULT_PERSONA_KEY;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && PERSONAS[stored]) return stored;
  } catch {
    /* localStorage blocked */
  }
  return DEFAULT_PERSONA_KEY;
}

export function LocaleProvider({ children }) {
  const [personaKey, setKey] = useState(readInitialPersonaKey);

  const persona = PERSONAS[personaKey] || PERSONAS[DEFAULT_PERSONA_KEY];
  const { locale, currency, rate } = persona;

  const setPersonaKey = useCallback((next) => {
    if (!PERSONAS[next]) return;
    setKey(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const t = useCallback((key) => tr(key, locale), [locale]);

  // Convert EUR amount -> persona currency, then format via Intl (handles
  // BRL "R$ 1.234,56", CNY "CN¥1.234,56", EUR "€1,000.00" etc. natively).
  const fmt = useCallback(
    (eurAmount) => {
      if (eurAmount === null || eurAmount === undefined || Number.isNaN(eurAmount)) return "—";
      const local = Number(eurAmount) * rate;
      try {
        return new Intl.NumberFormat(locale, {
          style: "currency",
          currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(local);
      } catch {
        return `${currency} ${local.toFixed(2)}`;
      }
    },
    [locale, currency, rate]
  );

  // v3.9.7 — Currency-preserving formatter for sub-supplier PunchOut lines.
  //
  // Contract:
  //   - Hosted-catalog lines are priced in EUR (backend source-of-truth) and
  //     MUST be FX-converted at render (EUR × persona.rate → persona.currency).
  //   - Sub-supplier PunchOut lines (Grainger USD, ZKH CNY, Motion USD,
  //     Lyreco EUR, Fastenal USD…) are returned in the partner's native
  //     currency. Applying persona.rate to them silently corrupts the price
  //     (a $410 Grainger line would render as "R$ 2,398.50" on the BR
  //     persona — pure fake FX). Sub-supplier lines are therefore
  //     rendered in their native currency with NO FX conversion.
  //
  // The `source_type` on every cart line / product / order line is the
  // authoritative signal ("sub_supplier_punchout" | "hosted"). The legacy
  // `fmt(amount)` remains untouched so hosted flows keep converting.
  const fmtMoney = useCallback(
    (amount, opts = {}) => {
      if (amount === null || amount === undefined || Number.isNaN(amount)) return "—";
      const isSubSupplier = opts.source_type === "sub_supplier_punchout";
      const explicitNative = opts.native === true;
      const lineCurrency = opts.currency || currency;
      if (isSubSupplier || explicitNative) {
        // Native display — never FX-converted.
        try {
          return new Intl.NumberFormat(locale, {
            style: "currency",
            currency: lineCurrency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(Number(amount));
        } catch {
          return `${lineCurrency} ${Number(amount).toFixed(2)}`;
        }
      }
      // Hosted lines fall through to the legacy EUR × rate path.
      return fmt(amount);
    },
    [locale, currency, fmt]
  );

  const value = useMemo(
    () => ({ persona, personaKey, locale, currency, rate, setPersonaKey, t, fmt, fmtMoney }),
    [persona, personaKey, locale, currency, rate, setPersonaKey, t, fmt, fmtMoney]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    // Defensive default — keeps storybook / isolated renders working.
    return {
      persona: PERSONAS[DEFAULT_PERSONA_KEY],
      personaKey: DEFAULT_PERSONA_KEY,
      locale: "en",
      currency: "EUR",
      rate: 1,
      setPersonaKey: () => {},
      t: (k) => tr(k, "en"),
      fmt: (v) =>
        v == null
          ? "—"
          : new Intl.NumberFormat("en", { style: "currency", currency: "EUR" }).format(v),
      fmtMoney: (v, opts = {}) => {
        if (v == null) return "—";
        const c = opts.currency || "EUR";
        try {
          return new Intl.NumberFormat("en", { style: "currency", currency: c }).format(v);
        } catch {
          return `${c} ${Number(v).toFixed(2)}`;
        }
      },
    };
  }
  return ctx;
}
